"use client";

import { useEffect, useCallback, useRef } from "react";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import Cookies from "js-cookie";
import { sendGtmEvent } from "~/lib/gtm/gE";
import useAuth from "~/services/auth/use-auth";
import { useUnauthorizedEventService } from "~/services/api/services/event";
import { sendWebEvent } from "~/lib/webevents/WebEventsClient";
import { hideChat, showChat } from "~/lib/intercom/IntercomScript";
import { QUIZ_ROUTES } from "~/constants/quizRoutes";
import { COOKIES } from "~/types/Cookie";
import { VERCEL_DEPLOYMENT_ID } from "~/services/api/config";
import useUserId from "~/hooks/useUserId";
import { useLogEvent } from "~/hooks/useLogEvent";
import useEvent from "~/hooks/useEvent";
import { sendFacebookEvent } from "~/lib/facebookEvents/FacebookEventsClient";
import { sendPinterestEvent } from "~/lib/pinterestEvents/PinterestEventsClient";
import { AppConfig } from "~/utils/AppConfig";
import { STORAGE_KEYS } from "~/types/Storage";
import { getStorageItem, setStorageItem } from "~/lib/storage/Storage";
import { APP_ROUTES } from "~/constants/appRoutes";
import { META_PIXELS } from "~/constants/metaPixels";
import { extractUserInfoFromFirstCompletedQuiz } from "~/components/Quiz/options";
import { trackFxEvent } from "~/lib/optimizely/trackFxEvent";
import { FX_EVENT_NAMES, FX_FUNNEL_PATHS, isFxLandingPage } from "~/lib/optimizely/fxEvents";

export const PATHNAME_HISTORY: string[] = [];

const PROTECTED_ROUTES = [
    "/p/help-center"
];

const OESTRA_LANDING_PAGES = [
    "/oestra-reasons",
    "/treatment/testosterone",
    "/product-page",
    "/treatment/hrt",
    "/treatment/perimenopause",
    "/treatment/menopause",
    "/treatment/endometriosis",
    "/treatment/pcos",
    "/treatment/postpartum",
    "/menopause/ad",
    "/perimenopause/ad"
];

const LIBIDA_LANDING_PAGES = [
    "/sexual-wellness/libida/arousal-cream-alternative",
    "/sexual-wellness/libida/pt-141",
    "/libida/7-reasons",
    "/sexual-wellness/libida",
    "/libida/ad"
];

const BODYMATCHED_LANDING_PAGES = [
    "/bodymatched/7-reasons"
];

const NAD_LANDING_PAGES = [
    "/nad"
];

let lastPathName = "empty";
let siteWideSent = false;

export function getReferringPage() {
    return PATHNAME_HISTORY[PATHNAME_HISTORY.length-2];
}

export function NavigationEvents() {
    const router = useRouter();
    const pathname = usePathname();
    const searchParams = useSearchParams();
    const { user, isLoaded } = useAuth();
    const sendBigQueryEvent = useUnauthorizedEventService();
    const { userId, isLoading: isUserIdLoading } = useUserId();
    const logEvent = useLogEvent();
    const initialSearchParamsRef = useRef(searchParams);

    const handleClick = useCallback((detail) => {
        if (!detail.pageX && !detail.clientX) return;

        void sendWebEvent(sendBigQueryEvent, {
            event_name: "click",
            event_type: "user_interaction",
            widget: "coordinates",
            meta_data: {
                element_id: detail.target.id || detail.target.innerText,
                x_axis: detail.pageX || detail.clientX,
                y_axis: detail.pageY || detail.clientY, 
            },
        });
    }, [sendBigQueryEvent]);

    useEvent("click", handleClick);
    useEvent("touchstart", handleClick);

    useEffect(() => {
        const params = initialSearchParamsRef.current;

        const docLocation = getStorageItem(STORAGE_KEYS.DOC_LOCATION);
        if (!docLocation || params.get("utm_source") || params.get("utm_medium") || params.get("utm_campaign")) {
            setStorageItem(STORAGE_KEYS.DOC_LOCATION, window.location.href);
        }

        const couponCode = params.get("coupon");
        if (couponCode) {
            setStorageItem(STORAGE_KEYS.COUPON, couponCode);
        }
    }, []);

    useEffect(() => {
        if (!isLoaded || user) return;

        const isProtectedRoute = PROTECTED_ROUTES.some(route => pathname.startsWith(route));
        if (!isProtectedRoute) return;

        const params = new URLSearchParams({
            returnTo: window.location.pathname,
        });

        router.replace(`/sign-in?${params.toString()}`);
    }, [pathname, router, user, isLoaded]);

    useEffect(() => {
        if (isLoaded && !siteWideSent && !isUserIdLoading) {
            siteWideSent = true;

            addEventListener("popstate", () => {
                void sendWebEvent(sendBigQueryEvent, {
                    event_name: "browser_navigation",
                    event_type: "user_interaction",
                    widget: "browser",
                });
            });

            const vercelCanaryId = Cookies.get(COOKIES.VERCEL_CANARY_ID);
            const environment = vercelCanaryId && vercelCanaryId === VERCEL_DEPLOYMENT_ID ? "rc" : AppConfig.isProduction ? "prod" : "qa";

            if (!userId) {
                logEvent({
                    message: "[NavigationEvents] userId is missing or null.",
                    data: {
                        event_name: "initial_sitewide",
                        widget: "sitewide",
                    },
                    tag: "userId-action",
                });
            }

            sendGtmEvent({
                event: "user_non_interaction",
                value: {
                    event_name: "initial_sitewide",
                    widget: "sitewide",
                    meta_data: {
                        session: {
                            initial_url: getStorageItem(STORAGE_KEYS.DOC_LOCATION),
                            environment: environment,
                        },
                        spec: {},
                        geo_location: {
                            country_code: Cookies.get(COOKIES.COUNTRY_CODE),
                        },
                        user: {
                            user_id: userId,
                            userAgent: navigator.userAgent,
                            ip_address_client: Cookies.get(COOKIES.IP_ADDRESS),
                        },
                        deploymentId: VERCEL_DEPLOYMENT_ID,
                    },
                },
            });

            void sendWebEvent(sendBigQueryEvent, {
                event_name: "initial_sitewide",
                event_type: "user_non_interaction",
                widget: "sitewide",
                meta_data: {
                    session: {
                        initial_url: getStorageItem(STORAGE_KEYS.DOC_LOCATION),
                    },
                    spec: {},
                    geo_location: {},
                    user: {
                        user_id: userId,
                    },
                },
            });
        }
    }, [isLoaded, sendBigQueryEvent, isUserIdLoading, logEvent, userId]);

    useEffect(() => {
        if (isLoaded && lastPathName !== pathname && !isUserIdLoading) {

            let type = "";
            const url = `${pathname}?${searchParams.toString()}`;
            if (lastPathName === "empty") {
                lastPathName =
                    document.referrer !== ""
                        ? new URL(document.referrer).pathname
                        : "";
            }

            let product_name = "";
            const pixels: string[] = [];
 
            if (pathname === "/pqp-oestra" || pathname === "/quiz/hormone-imbalance" || pathname === "/quiz/endometriosis" || pathname === "/quiz/postpartum") {
                pixels.push(META_PIXELS.OESTRA_CREAM);
                product_name = "oestra-cream";
            } else if(pathname === "/pqp-libida" || pathname === "/quiz/libida") {
                pixels.push(META_PIXELS.LIBIDA);
                product_name = "libida";
            } else if(pathname === "/pqp/anti-aging-face-cream" || pathname === "/quiz/anti-aging-face-cream") {
                pixels.push(META_PIXELS.FACE_CREAM);
                product_name = "face-cream";
            } else if(pathname === "/pqp-nad" || pathname=== "/quiz/nad") {
                //pixels.push(META_PIXELS.NAD);
                product_name = "nad";
            }

            sendFacebookEvent(sendBigQueryEvent, {
                event_name: "Fullback",
            }, pixels.splice(0));

            if (OESTRA_LANDING_PAGES.includes(pathname)) {
                pixels.push(META_PIXELS.OESTRA_CREAM);
                product_name = "oestra-cream";
            } else if(LIBIDA_LANDING_PAGES.includes(pathname)) {
                pixels.push(META_PIXELS.LIBIDA);
                product_name = "libida";
            } else if(BODYMATCHED_LANDING_PAGES.includes(pathname)) {
                pixels.push(META_PIXELS.FACE_CREAM);
                product_name = "face-cream";
            } else if(NAD_LANDING_PAGES.includes(pathname)) {
                // pixels.push(META_PIXELS.NAD);
                product_name = "nad";
            }

            sendFacebookEvent(sendBigQueryEvent, {
                event_name: "Jersey",
            }, pixels.splice(0));

            if (pathname === "/pqp-oestra") {
                pixels.push(META_PIXELS.OESTRA_CREAM);
            } else if(pathname === "/pqp-libida") {
                pixels.push(META_PIXELS.LIBIDA);
            } else if(pathname === "/pqp/anti-aging-face-cream") {
                pixels.push(META_PIXELS.FACE_CREAM);
            } else if(pathname === "/pqp-nad") {
                // pixels.push(META_PIXELS.NAD);
            }

            const quizData = extractUserInfoFromFirstCompletedQuiz();

            sendFacebookEvent(sendBigQueryEvent, {
                event_name: "Substitute",
                email: quizData.email,
                first_name: quizData.first_name,
                last_name: quizData.last_name,
                phone: quizData.phone,
            }, pixels.splice(0));

            if (product_name !== "") {
                sendPinterestEvent(sendBigQueryEvent, {
                    event_name: "pagevisit",
                    email: quizData.email,
                    first_name: quizData.first_name,
                    last_name: quizData.last_name,
                    phone: quizData.phone,
                    custom_data: {
                        line_items: [
                            {
                                product_name: product_name,
                            }
                        ]
                    }
                });
            }

            if (pathname.includes("/p/")) {
                type = "lp";
                if (pathname.includes("/p/learn")) {
                    type = "journal";
            
                    sendFacebookEvent(sendBigQueryEvent, {
                        event_name: "ViewContent",
                    });
                }
            } else {
                switch (pathname) {
                    case "/": {
                        type = "homepage";
                        showChat();
                        break;
                    }
                    case QUIZ_ROUTES.HORMONE_IMBALANCE: {
                        hideChat();
                        type = "quiz";
                        break;
                    }
                    case APP_ROUTES.WEBINAR: {
                        hideChat();
                        type = "webinar";
                        break;
                    }
                    case APP_ROUTES.PQP_HORMONE_IMBALANCE: {
                        type = "post-quiz";
                        break;
                    }
                    case APP_ROUTES.CHECKOUT:
                        type = "checkout";
                        break;
                    default:
                        type = pathname.replaceAll("/", "");
                        break;
                }
            }

            sendGtmEvent({
                event: "user_non_interaction",
                value: {
                    event_name: "virtual_pageview",
                    widget: type,
                    meta_data: {
                        url,
                        title: document.title,
                        type,
                        referring_page: lastPathName,
                    },
                },
            });

            void sendWebEvent(sendBigQueryEvent, {
                event_name: "virtual_pageview",
                event_type: "user_non_interaction",
                widget: type,
                meta_data: {
                    url,
                    title: document.title,
                    type,
                    referring_page: lastPathName,
                },
            });

            // FX funnel entry. lastPathName is read before it advances below.
            if (pathname.includes(FX_FUNNEL_PATHS.QUIZ)) {
                if (isFxLandingPage(lastPathName)) {
                    trackFxEvent({ event_name: FX_EVENT_NAMES.LP_TO_QUIZ, properties: { itemSlug: product_name } });
                } else if (lastPathName.includes(FX_FUNNEL_PATHS.PQP_QUIZ)) {
                    trackFxEvent({ event_name: FX_EVENT_NAMES.PQP_TO_QUIZ });
                }
            }

            lastPathName = pathname;
            PATHNAME_HISTORY.push(pathname);
        }
    }, [pathname, searchParams, isLoaded, user, isUserIdLoading, sendBigQueryEvent]);

    return null;
}